package fr.labodoc.healthProfessional.webApp.pages.medicines

import fr.labodoc.healthProfessional.webApp.App
import fr.labodoc.healthProfessional.webApp.Page
import fr.labodoc.healthProfessional.webApp.components.hr
import fr.labodoc.healthProfessional.webApp.components.labodocSpinner
import fr.labodoc.healthProfessional.webApp.components.navigoLink
import fr.labodoc.healthProfessional.webApp.navigate
import fr.labodoc.healthProfessional.webApp.pages.components.MessageModal
import fr.labodoc.require
import fr.labodoc.utils.dateFormat
import io.kvision.core.Container
import io.kvision.core.onChange
import io.kvision.core.onClick
import io.kvision.core.onEvent
import io.kvision.form.check.checkBox
import io.kvision.html.*
import io.kvision.panel.SimplePanel
import io.kvision.state.bind
import io.kvision.toast.Toast
import io.kvision.utils.event
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.koin.core.component.KoinComponent
import org.koin.core.component.inject
import org.koin.core.parameter.parametersOf
import org.w3c.dom.Node

class MedicinePage(medicineId: String) : SimplePanel(), KoinComponent {
  private val viewModel: MedicinePageViewModel by inject { parametersOf(medicineId) }

  init {
    id = "page-medicine-page"
    require("./css/pages/medicines/medicine.css")

    div(className = "page-width").bind(viewModel.medicineUiState) { uiState ->
      when (uiState) {
        is MedicinePageViewModel.MedicineUiState.Loading -> {
          labodocSpinner()
        }

        is MedicinePageViewModel.MedicineUiState.Error -> {
          Toast.danger("Ce médicament n'existe pas")
          App.Companion.routing.navigate(Page.Dashboard())
        }

        is MedicinePageViewModel.MedicineUiState.Loaded -> {
          informations(uiState.medicine, uiState.laboratory)

          hr()

          div(className = "name-me") {
            div(className = "resources") {
              if (uiState.marketingDocuments.isNotEmpty())
                marketingDocuments(uiState.marketingDocuments)

              dosages(uiState.dosages)
            }

            links(uiState.medicine.name, uiState.laboratory?.websiteUrl)

            add(
              MessageList(
                messageListUiState = viewModel.messageListUiState,
                messageListEvents = viewModel.messageListEvents,
                onMessageClick = viewModel::getMessage,
                onMessageFavoriteClick = viewModel::setMessageFavoriteStatus
              )
            )
          }
        }
      }
    }
  }

  private fun Container.informations(
    medicine: MedicinePageViewModel.Medicine,
    laboratory: MedicinePageViewModel.Laboratory?
  ): Div {
    return div(className = "informations-section") {
      image(medicine.logoUrl)

      h1(medicine.name, className = "name")

      laboratory?.let { laboratory ->
        p(laboratory.name, className = "laboratory")
      }

      ol(className = "atc-classification") {
        medicine.atcClassification.firstLevel?.let {
          li(className = "level first", rich = true) {
            navigoLink("", Page.Dashboard(it.code)) {
              span(it.code, className = "code")
              span(it.name, className = "name")
            }
          }
        }

        medicine.atcClassification.secondLevel?.let {
          li(className = "level second", rich = true) {
            navigoLink("", Page.Dashboard(it.code)) {
              span(it.code, className = "code")
              span(it.name, className = "name")
            }
          }
        }

        medicine.atcClassification.thirdLevel?.let {
          li(className = "level third", rich = true) {
            navigoLink("", Page.Dashboard(it.code)) {
              span(it.code, className = "code")
              span(it.name, className = "name")
            }
          }
        }

        medicine.atcClassification.fourthLevel?.let {
          li(className = "level fourth", rich = true) {
            navigoLink("", Page.Dashboard(it.code)) {
              span(it.code, className = "code")
              span(it.name, className = "name")
            }
          }
        }

        medicine.atcClassification.fifthLevel?.let {
          li(className = "level fifth", rich = true) {
            navigoLink("", Page.Dashboard(it.code)) {
              span(it.code, className = "code")
              span(it.name, className = "name")
            }
          }
        }
      }
    }
  }

  private fun Container.marketingDocuments(marketingDocuments: List<MedicinePageViewModel.MarketingDocument>): Div {
    return div(className = "documents-section") {
      h2("Documents du laboratoire", className = "title")

      p("Accédez aux derniers documents fournis par le laboratoire", className = "description")

      ul(className = "documents") {
        marketingDocuments.forEachIndexed { index, marketingDocument ->
          li(className = "document") {
            if (!marketingDocument.seenLatestVersion)
              addCssClass("new")

            link(
              label = marketingDocument.name,
              url = marketingDocument.url,
              "fa-solid fa-file-arrow-down",
              target = "_blank"
            ) {
              onEvent {
                event("auxclick") {
                  viewModel.tagDocumentAsSeen(marketingDocument.id)
                }

                click = {
                  viewModel.tagDocumentAsSeen(marketingDocument.id)
                }
              }
            }
          }
        }
      }
    }
  }

  private fun Container.dosages(dosages: List<MedicinePageViewModel.Dosage>): Div {
    return div(className = "dosages-section") {
      h2("Liste des spécialités pharmaceutiques", className = "title")

      p("Accédez aux fiches information de la Base de données publique du médicament", className = "description")

      div(className = "scroll-container") {
        ul(className = "dosages") {
          dosages.forEach { dosage ->
            li(className = "dosage") {
              link(
                label = dosage.name,
                url = "https://base-donnees-publique.medicaments.gouv.fr/medicament/${dosage.cisCode}/extrait",
                target = "_blank"
              )
            }
          }
        }
      }
    }
  }

  private fun Container.links(medicineName: String, laboratoryWebsiteUrl: String?): Div {
    return div(className = "links-section") {
      h2("Vous souhaitez plus d'informations ?", className = "title")

      div(className = "links") {
        laboratoryWebsiteUrl?.let {
          link(
            label = "Consultez le site du laboratoire",
            url = laboratoryWebsiteUrl,
            target = "_blank",
            className = "link laboratory-website",
            labelFirst = false
          ) {
            image(require("./img/page-medicine-laboratory-website-icon.svg"))
          }
        }

        link(
          label = "Publications HAS",
          url = "https://www.has-sante.fr/jcms/fc_2875171/fr/resultat-de-recherche?text=${medicineName}",
          target = "_blank",
          className = "link has-website",
          labelFirst = false
        ) {
          image(require("./img/page-medicine-has-icon.png"))
        }

        link(
          label = "Publications ANSM",
          url = "https://ansm.sante.fr/resultats-de-recherche?filter%5BsafetyNewsSearch%5D=1&filter%5Bmatching%5D=k&filter%5BstartDate%5D=&filter%5BendDate%5D=&filter%5Btext%5D=${medicineName}",
          target = "_blank",
          className = "link ansm-website",
          labelFirst = false
        ) {
          image(require("./img/page-medicine-ansm-icon.png"))
        }
      }
    }
  }
}

private class MessageList(
  messageListUiState: StateFlow<MedicinePageViewModel.MessageListUiState>,
  messageListEvents: SharedFlow<MedicinePageViewModel.MessageEvent>,
  onMessageClick: (String) -> Unit,
  onMessageFavoriteClick: (String, Boolean) -> Unit
) : SimplePanel("message-list") {
  init {
    App.Companion.scope.launch {
      messageListEvents.collect { event ->
        when (event) {
          MedicinePageViewModel.MessageEvent.SetFavoriteStatusFailed -> {
            Toast.danger("Une erreur est survenue lors de la mise à jour du favori")
          }

          is MedicinePageViewModel.MessageEvent.OpenMessage -> {
            MessageModal(
              message = event.message,
              onFavoriteClicked = {
                onMessageFavoriteClick(event.message.id, it)
              }
            ).show()
          }
        }
      }
    }

    bind(messageListUiState) { uiState ->
      when (uiState) {
        is MedicinePageViewModel.MessageListUiState.Loading -> {
          labodocSpinner()
        }

        is MedicinePageViewModel.MessageListUiState.Error -> {
          p("FIX ME") // TODO
        }

        is MedicinePageViewModel.MessageListUiState.Loaded -> {
          val messages = uiState.messages.values

          div(className = "messages") {
            if (messages.isEmpty())
              addCssClass("empty")

            header {
              val newMessagesNumber = messages.count { !it.seen }

              icon("fa-solid fa-envelope") {
                if (newMessagesNumber > 0)
                  span(newMessagesNumber.toString(), className = "message-number")
              }

              div {
                if (messages.isNotEmpty())
                  p("Actualité sur ce médicament", className = "title")
                else {
                  p("Actuellement, il n’y a pas d'actualité", className = "title")
                  p("Vous serez alerté en cas de nouveaux messages", className = "tip")
                }
              }
            }

            div(className = "list") {
              messages
                .forEach { message ->
                  messageRow(
                    message = message,
                    onClick = {
                      onMessageClick(message.id)
                    },
                    onFavoriteClicked = {
                      onMessageFavoriteClick(message.id, it)
                    }
                  )
                }
            }
          }
        }
      }
    }
  }

  private fun Container.messageRow(
    message: MedicinePageViewModel.Message,
    onClick: () -> Unit,
    onFavoriteClicked: (Boolean) -> Unit,
  ): Div {
    return div(className = "message") {
      if (!message.seen)
        addCssClass("new")
      else
        removeCssClass("new")

      onClick {
        onClick()
      }

      p(className = "type") {
        rich = true
        content = when (message.type) {
          MedicinePageViewModel.Message.Type.FlashInfo -> """<i class="fa-solid fa-bell"></i> Flash Info"""
          MedicinePageViewModel.Message.Type.Invitation -> """<i class="fa-solid fa-envelope"></i> Invitation"""
        }

        if (!message.seen)
          +""" <i class="fa-solid fa-exclamation"></i>"""
      }

      div {
        val favoriteCheckbox = checkBox {
          addCssClass("favorite")
          value = message.favorite

          onChange { event ->
            event.stopPropagation()
            onFavoriteClicked(value)
          }
        }

        p(message.title, className = "title")

        onClick { event ->
          val target = event.target

          if (target is Node && favoriteCheckbox.getElement()!!.contains(target))
            event.stopPropagation()
        }
      }

      p(message.publishedAt.dateFormat, className = "created-date")
    }
  }
}

fun Container.medicinePage(medicineId: String): MedicinePage {
  val medicinePage = MedicinePage(medicineId)

  this.add(medicinePage)
  return medicinePage
}
